import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import { HiOutlineSearch, HiOutlineLocationMarker, HiOutlineHome } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import videoBg from '../../assest/video.mp4'; 
import './Hero.css';

const Hero = () => {
  const navigate = useNavigate();
  
  const [availableCities, setAvailableCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [propertiesCount, setPropertiesCount] = useState({
    total: 0,
    forSale: 0,
    forRent: 0
  });

  const typeTranslations = {
    house: 'بيت',
    apartment: 'شقة',
    land: 'أرض',
  };

  const [searchParams, setSearchParams] = useState({
    city: '',
    type: 'house', 
    status: 'sale'    
  });

  useEffect(() => {
    fetchCitiesAndStats();
  }, []);

  const fetchCitiesAndStats = async () => {
    try {
      setStatsLoading(true);
      
      const { data: properties, error } = await supabase
        .from('properties')
        .select('city')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Supabase error:', error);
        setAvailableCities([]);
        setPropertiesCount({ total: 0, forSale: 0, forRent: 0 });
        return;
      }
      
      if (!properties || properties.length === 0) {
        setAvailableCities([]);
        setPropertiesCount({ total: 0, forSale: 0, forRent: 0 });
        return;
      }
      
      const cityCounts = {};
      const saleCounts = {};
      const rentCounts = {};
      let total = 0;
      let forSale = 0;
      let forRent = 0;
      
      properties.forEach(property => {
        if (property.city?.ar) {
          const cityName = property.city.ar;
          cityCounts[cityName] = (cityCounts[cityName] || 0) + 1;
          
          if (property.ownership === 'sale') {
            saleCounts[cityName] = (saleCounts[cityName] || 0) + 1;
            forSale++;
          } else {
            rentCounts[cityName] = (rentCounts[cityName] || 0) + 1;
            forRent++;
          }
          total++;
        }
      });
      
      const sortedCities = Object.keys(cityCounts)
        .map(city => ({
          name: city,
          count: cityCounts[city],
          saleCount: saleCounts[city] || 0,
          rentCount: rentCounts[city] || 0
        }))
        .sort((a, b) => b.count - a.count); 
      
      const limitedCities = sortedCities.slice(0, 1000).map(city => city.name);
      
      setAvailableCities(limitedCities);
      setPropertiesCount({ total, forSale, forRent });
      
    } catch (err) {
      console.error('Unexpected error:', err);
      setAvailableCities([]);
      setPropertiesCount({ total: 0, forSale: 0, forRent: 0 });
    } finally {
      setStatsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    
    let url = `/properties/${searchParams.type}/${searchParams.status}`;
    
    if (searchParams.city) {
      url += `?city=${encodeURIComponent(searchParams.city)}`;
    }
    
    navigate(url);
    
    setTimeout(() => {
      setLoading(false);
    }, 300);
  };

  return (
    <section className="hero-section" dir="rtl">
      <video className="hero-video" autoPlay loop muted playsInline>
        <source src={videoBg} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      <div className="hero-overlay"></div>

      <Container className="hero-content-container d-flex flex-column align-items-center justify-content-center h-100">
        <div className="text-center text-white mb-4 hero-text-animate">
          <h1 className="hero-main-title fw-bold mb-3">اعثر على بيتك المثالي</h1>
          <p className="hero-sub-title opacity-75">
            {statsLoading 
              ? 'جاري تحميل العروض العقارية...' 
              : propertiesCount.total > 0 
                ? `اكتشف ${propertiesCount.total} عقار للبيع والإيجار`
                : 'ابدأ رحلتك للعثور على عقارك المثالي'
            }
          </p>
        </div>

        <div className="search-wrapper shadow-lg">
          <Form onSubmit={handleSearch}>
            <Row className="g-0 align-items-center bg-white rounded-inner">
              
              <Col xs={12} lg={4} className="search-col border-left-lg border-bottom-mobile">
                <div className="search-item">
                  <HiOutlineLocationMarker className="search-icon" />
                  <Form.Select 
                    className="search-select border-0 shadow-none bg-transparent"
                    value={searchParams.city}
                    onChange={(e) => setSearchParams({...searchParams, city: e.target.value})}
                    disabled={loading || statsLoading}
                    size={availableCities.length > 5 ? "sm" : ""}
                    style={{
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}
                  >
                    <option value="">كل المدن ({availableCities.length})</option>
                    {availableCities.map((cityName, index) => (
                      <option key={`city-${index}`} value={cityName}>
                        {cityName}
                      </option>
                    ))}
                    {availableCities.length === 0 && !statsLoading && (
                      <option disabled>لا توجد مدن متاحة</option>
                    )}
                  </Form.Select>
                </div>
              </Col>

              <Col xs={6} lg={3} className="search-col border-left-lg">
                <div className="search-item">
                  <HiOutlineHome className="search-icon" />
                  <Form.Select 
                    className="search-select border-0 shadow-none bg-transparent"
                    value={searchParams.type}
                    onChange={(e) => setSearchParams({...searchParams, type: e.target.value})}
                    disabled={loading}
                  >
                    {Object.entries(typeTranslations).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </Col>

              <Col xs={6} lg={2} className="search-col">
                <div className="search-item">
                  <Form.Select 
                    className="search-select border-0 shadow-none bg-transparent"
                    value={searchParams.status}
                    onChange={(e) => setSearchParams({...searchParams, status: e.target.value})}
                    disabled={loading}
                  >
                    <option value="sale">للبيع</option>
                    <option value="rent">للايجار</option>
                  </Form.Select>
                </div>
              </Col>

              <Col xs={12} lg={3} className="p-2">
                <Button 
                  type="submit"
                  className="search-submit-btn w-100 d-flex align-items-center justify-content-center"
                  disabled={loading || statsLoading}
                >
                  {loading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      جاري البحث...
                    </>
                  ) : (
                    <>
                      <HiOutlineSearch className="me-2" />
                      بحث
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </div>

       
      </Container>
    </section>
  );
};

export default Hero;